/**
 * RiskIssueList — the exposure summary panel atop the Risk surface. A 3×3
 * probability/impact heat-map of the program's OPEN risks, beside four register
 * stats (Open Risks · Mitigating · Open Issues · Closed YTD), so a PM reads the
 * live posture without opening a sub-tab.
 *
 * Projection-gated (no_view → nothing) but NOT field-masked: risk/issue posture
 * is operational, shown to every viewer who can see the program.
 */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Skeleton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { programRef } from '../../../lib/access/types';
import { useView } from '../../../lib/access/useView';
import { isClosed, isHigh, isMed } from './severity';
import { useProgramRisksIssues } from './useProgramRisksIssues';
// Low → 0, Med → 1, High → 2. Drives both matrix placement and the axis order.
const level = (s)=>isHigh(s) ? 2 : isMed(s) ? 1 : 0;
const CELL_SEV = [
    [
        'warning',
        'orange',
        'danger'
    ],
    [
        'neutral',
        'warning',
        'orange'
    ],
    [
        'neutral',
        'neutral',
        'warning'
    ]
];
// Every cell shares one uniform hairline border (the mockup's matrixLine =
// rgba(foreground, 0.1)); only the fill + text change by severity. Neutral
// (low-severity) cells stay transparent — only red/orange/amber quadrants tint.
const SEV_CLS = {
    danger: 'bg-danger-muted text-danger',
    orange: 'bg-orange-muted text-orange',
    warning: 'bg-warning-muted text-warning',
    neutral: 'text-muted-foreground'
};
const AXIS = 'text-[0.6rem] font-medium uppercase tracking-wide text-muted-foreground';
function Panel({ children }) {
    return /*#__PURE__*/ _jsx("div", {
        className: "rounded-lg bg-muted p-5",
        "data-testid": Testids.deliveryRisk.riskIssueList,
        children: children
    });
}
export function RiskIssueList({ programId }) {
    const view = useView(programRef(programId));
    const { risks, issues, isLoading, isError } = useProgramRisksIssues(programId);
    if (view.viewMode === 'no_view') return null;
    if (isError) {
        return /*#__PURE__*/ _jsx(Panel, {
            children: /*#__PURE__*/ _jsx("p", {
                className: "text-sm text-danger",
                children: "Couldn't load risks & issues."
            })
        });
    }
    if (isLoading) {
        return /*#__PURE__*/ _jsx(Panel, {
            children: /*#__PURE__*/ _jsx(Skeleton, {
                className: "h-28 w-full"
            })
        });
    }
    const openRisks = risks.filter((r)=>!isClosed(r.status));
    const openIssues = issues.filter((i)=>!isClosed(i.state));
    // 3×3 counts: row = 2 − impactLevel (High on top), col = probabilityLevel.
    const counts = [
        [
            0,
            0,
            0
        ],
        [
            0,
            0,
            0
        ],
        [
            0,
            0,
            0
        ]
    ];
    for (const r of openRisks)counts[2 - level(r.impact)][level(r.probability)] += 1;
    const cells = counts.flatMap((rowArr, ri)=>rowArr.map((n, ci)=>({
                key: `${ri}-${ci}`,
                n,
                sev: CELL_SEV[ri][ci]
            })));
    const nOpen = openRisks.filter((r)=>/^\s*open\s*$/i.test(r.status)).length;
    const nMit = openRisks.filter((r)=>/mitigat/i.test(r.status)).length;
    const nClosed = risks.filter((r)=>isClosed(r.status)).length;
    const nOpenIss = openIssues.length;
    const nHH = openRisks.filter((r)=>isHigh(r.impact) && isHigh(r.probability)).length;
    const nCrit = openIssues.filter((i)=>i.highRiskFlag || /critical/i.test(i.priority)).length;
    const stats = [
        {
            name: 'Open Risks',
            value: nOpen,
            note: nHH ? `${nHH} high-high` : 'none high-high',
            danger: nOpen > 0
        },
        {
            name: 'Mitigating',
            value: nMit,
            note: 'plans in motion'
        },
        {
            name: 'Open Issues',
            value: nOpenIss,
            note: nCrit ? `${nCrit} critical` : 'none critical'
        },
        {
            name: 'Closed YTD',
            value: nClosed,
            note: 'risk register',
            last: true
        }
    ];
    return /*#__PURE__*/ _jsx(Panel, {
        children: /*#__PURE__*/ _jsxs("div", {
            className: "grid gap-8 md:grid-cols-[230px_minmax(0,1fr)] md:items-start",
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: "text-[0.6rem] font-medium uppercase tracking-[0.08em] text-muted-foreground",
                            children: "Exposure — Open Risks"
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "mt-3 flex gap-2",
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: `flex flex-col justify-between py-0.5 text-right ${AXIS}`,
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            children: "High"
                                        }),
                                        /*#__PURE__*/ _jsx("span", {
                                            children: "Med"
                                        }),
                                        /*#__PURE__*/ _jsx("span", {
                                            children: "Low"
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    className: "grid flex-1 grid-cols-3 grid-rows-[40px_40px_40px] gap-[3px]",
                                    children: cells.map((c)=>/*#__PURE__*/ _jsx("div", {
                                            className: `flex items-center justify-center rounded-[4px] border border-foreground/10 text-[12.5px] font-medium tabular-nums ${c.n ? SEV_CLS[c.sev] : 'text-muted-foreground'}`,
                                            children: c.n || ''
                                        }, c.key))
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: `ml-[38px] mt-1.5 flex justify-between ${AXIS}`,
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    children: "Low"
                                }),
                                /*#__PURE__*/ _jsx("span", {
                                    children: "Med"
                                }),
                                /*#__PURE__*/ _jsx("span", {
                                    children: "High"
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: "ml-[38px] mt-1 text-[0.625rem] text-muted-foreground",
                            children: "impact ↑ · probability →"
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "grid grid-cols-2 sm:grid-cols-4",
                    children: stats.map((s)=>/*#__PURE__*/ _jsxs("div", {
                            className: s.last ? '' : 'sm:mr-[18px] sm:border-r sm:border-border sm:pr-[18px]',
                            children: [
                                /*#__PURE__*/ _jsx("div", {
                                    className: "text-[0.6rem] font-medium uppercase tracking-[0.07em] text-muted-foreground",
                                    children: s.name
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    className: `mt-1.5 font-display text-2xl font-normal leading-none tabular-nums ${s.danger ? 'text-danger' : 'text-foreground'}`,
                                    children: s.value
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    className: "mt-1.5 text-xs text-muted-foreground",
                                    children: s.note
                                })
                            ]
                        }, s.name))
                })
            ]
        })
    });
}
